<!-- List of permission types that can be toggled -->
<template>
	<div v-if="permissionTypes" class="flex w-full flex-col">
		<div
			v-for="group in permissionGroups"
			:key="group"
			class="mb-3 flex w-full flex-col"
		>
			<div class="text-sm text-gray-600">{{ group }}</div>
			<ion-item
				v-for="permission in groupedPermissions[group]"
				:key="permission.key"
				class="rounded-lg transition-colors hover:bg-gray-200"
				:class="{
					'opacity-50': disabledPermissions?.includes(permission.key),
				}"
				color="transparent"
				lines="none"
				button
				:detail="false"
				:disabled="disabledPermissions?.includes(permission.key)"
				@click="togglePermissionSelection(permission.key)"
			>
				<ion-checkbox
					slot="start"
					:checked="selection.includes(permission.key)"
					:disabled="disabledPermissions?.includes(permission.key)"
				/>
				<ion-label>
					<div class="flex items-center gap-2">
						{{ permission.name }}
						<ion-icon
							v-if="disabledPermissions?.includes(permission.key)"
							:icon="lockClosedOutline"
							class="text-gray-500"
						/>
					</div>
					<div
						v-if="disabledPermissions?.includes(permission.key)"
						class="text-xs text-gray-500"
					>
						{{ $t("user-management.inherited-from-role") }}
					</div>
				</ion-label>
			</ion-item>
		</div>
	</div>
</template>

<script setup lang="ts">
	import _ from "lodash";
	import { usePermissionTypes } from "seedgreen-shared/queries/permission-type";
	import { computed } from "vue";
	import { IonCheckbox, IonItem, IonIcon, IonLabel } from "@ionic/vue";
	import { PermissionKey } from "seedgreen-shared/dtos/PermissionTypeDTO";
	import { lockClosedOutline } from "ionicons/icons";

	const props = defineProps<{
		showInternal?: boolean;
		disabledPermissions?: PermissionKey[];
	}>();

	const selection = defineModel<PermissionKey[]>("modelValue", {
		required: true,
	});

	const { permissionTypes } = usePermissionTypes();

	const groupedPermissions = computed(() => {
		const permissions = permissionTypes.value ?? [];
		if (!props.showInternal) {
			return _.groupBy(
				permissions.filter((p) => !p.internalOnly),
				"group",
			);
		}
		return _.groupBy(permissions, "group");
	});

	// Sort the permission groups
	const groupOrder = ["", "Tasks", "Schedules", "Administration", "Internal"];
	const permissionGroups = computed(() => {
		if (!groupedPermissions.value) return [];
		return Object.keys(groupedPermissions.value)
			.map((g) => g ?? "")
			.sort((a, b) => {
				const indexA = groupOrder.indexOf(a);
				const indexB = groupOrder.indexOf(b);

				if (indexA >= 0 && indexB >= 0) {
					return indexA - indexB;
				}
				if (indexA >= 0) {
					return -1;
				}
				if (indexB >= 0) {
					return 1;
				}
				return 0;
			});
	});

	function togglePermissionSelection(permission: PermissionKey) {
		if (props.disabledPermissions?.includes(permission)) {
			return;
		}

		if (selection.value.includes(permission)) {
			selection.value = selection.value.filter((p) => p !== permission);
		} else {
			selection.value = [...selection.value, permission];
		}
	}
</script>
